<template>
  <div
    class="detail-item"
    :class="{ 'detail-item--copyable': copyable, 'detail-item--wide': wide }"
    :data-replay-sensible="sensible ? '' : undefined"
    @click="handleCopy"
  >
    <DetailLabel v-if="label" tag="label" @click="focusField">{{ label }}</DetailLabel>
    <div ref="valueRef" class="value">
      <span class="value-content">
        <slot>
          <template v-if="hasValue">{{ value }}</template>
          <DetailEmpty v-else />
        </slot>
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import DetailLabel from './DetailLabel.vue'
import DetailEmpty from './DetailEmpty.vue'

const props = defineProps({
  label: {
    type: String,
    default: null
  },
  value: {
    type: [String, Number],
    default: null
  },
  copyable: {
    type: Boolean,
    default: true
  },
  copyValue: {
    type: String,
    default: null
  },
  /**
   * Occupe la largeur entière de `.detail-grid` au lieu d'une colonne.
   *
   * Pour les valeurs qui n'ont pas de fin naturelle : un commentaire libre,
   * une adresse email longue, un intitulé de formation. Coupées en demi-ligne
   * elles se replient sur trois hauteurs et désalignent toute la grille.
   * Trois cartes de la fiche demande portaient chacune leur propre
   * `.detail-item-full` avant que la règle ne monte ici.
   */
  wide: {
    type: Boolean,
    default: false
  },
  /**
   * Valeur masquée en entier dans le replay de PostHog : adresse d'une
   * personne, IBAN, date de naissance, handicap, santé
   * (cf. utils/posthog-masquage.js).
   */
  sensible: {
    type: Boolean,
    default: false
  }
})

const valueRef = ref(null)
const justCopied = ref(false)

const hasValue = computed(() => props.value !== null && props.value !== undefined && props.value !== '')

const INTERACTIVE_SELECTORS = 'input, select, textarea, button, a, [contenteditable="true"]'
const EMPTY_VALUES = ['—', '-', '']

// Une ligne qui ne porte que « Non renseigné » n'a rien à copier : on
// copierait le mot de l'absence, pas une donnée.
function isOnlyEmpty(content) {
  const empty = content?.querySelector('.detail-empty')
  return !!empty && content.innerText.trim() === empty.innerText.trim()
}

// Le champ vit dans le slot : impossible de le viser avec un <label for>, on
// résout donc la cible au moment du clic.
const FIELD_SELECTORS = 'input, select, textarea, [contenteditable="true"]'

function focusField(event) {
  const field = valueRef.value?.querySelector(FIELD_SELECTORS)
  if (!field || field.disabled || field.readOnly) return

  // Le libellé se comporte comme un <label for> : il donne le focus au champ,
  // et la ligne ne part pas en copie presse-papier.
  event.stopPropagation()
  field.focus()

  // Sur un <select>, un clic sur le libellé déroule la liste comme un clic
  // sur le champ lui-même (navigateurs qui exposent showPicker).
  if (field.tagName === 'SELECT' && typeof field.showPicker === 'function') {
    try {
      field.showPicker()
    } catch {
      // Le navigateur refuse hors geste utilisateur direct : le focus suffit.
    }
  }
}

async function handleCopy(event) {
  if (!props.copyable) return

  if (event.target.closest(INTERACTIVE_SELECTORS)) return
  if (!props.copyValue && isOnlyEmpty(valueRef.value?.querySelector('.value-content'))) return

  let textToCopy = props.copyValue
  if (!textToCopy && props.value != null) {
    textToCopy = String(props.value)
  }
  if (!textToCopy && valueRef.value) {
    textToCopy = valueRef.value.querySelector('.value-content')?.innerText?.trim()
  }

  if (!textToCopy || EMPTY_VALUES.includes(textToCopy)) return

  try {
    await navigator.clipboard.writeText(textToCopy)
    toast.success('Copié dans le presse-papier')
    justCopied.value = true
    setTimeout(() => { justCopied.value = false }, 1500)
  } catch (err) {
    toast.error('Erreur lors de la copie')
  }
}
</script>

<style scoped>
/* Ligne de champ : libellé au-dessus, valeur en dessous, filet de séparation.
   Deux champs tiennent côte à côte dans une colonne de fiche (cf. .detail-grid
   de CollapsibleSection), ce qui divise par deux la hauteur d'une section. */
.detail-item {
  padding: var(--spacing-2) 0;
  background: none;
  border: none;
  border-bottom: 1px solid var(--color-border-light);
  border-radius: 0;
  min-width: 0;
}

/* Le champ traverse toute la grille de `CollapsibleSection`, quel que soit le
   nombre de colonnes qu'elle a résolu. */
.detail-item--wide {
  grid-column: 1 / -1;
}

/* Le libellé est un `DetailLabel` : son style vit là-bas, partagé avec les
   intitulés de groupe posés hors d'une ligne de champ. */

.detail-item .value {
  display: flex;
  align-items: baseline;
  gap: var(--spacing-2);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  min-width: 0;
  padding-right: 0;
}

.value-content {
  min-width: 0;
}

/* Copie au clic : rien n'est écrit dans la ligne, le survol la signale et le
   toast confirme. */
.detail-item--copyable {
  cursor: pointer;
}

.detail-item--copyable:hover .value {
  color: var(--color-primary);
}

/* « Non renseigné » ne se copie pas : la ligne ne promet donc pas de le faire. */
.detail-item--copyable:has(.detail-empty):not(:has(input, select, textarea)) {
  cursor: default;
}

/* En édition, le champ occupe toute la largeur. */
.detail-item .value:has(input, select, textarea) {
  display: block;
  cursor: auto;
}

/* Le libellé est cliquable dès qu'il y a un champ à activer. */
.detail-item:has(input, select, textarea) label {
  cursor: pointer;
}
</style>
